/**
 * Shift-script grammar (IMPLEMENTATION_PLAN.md E4).
 *
 * A shift is a declarative list of beats. The same grammar drives scripted story moments
 * and Endless mode's generator, and — critically — every trigger carries a timeout so a
 * shift can never wedge waiting for something that died, retreated or never arrived.
 */

import type { VesselKind } from '../sim/types';
import type { WeatherPresetName } from '../sim/weather';

/** When a beat fires. */
export type Trigger =
  /** Absolute seconds since the shift started. */
  | { at: number }
  /** When a named earlier spawn gets within `km` of the rig. */
  | { whenWithin: { tag: string; km: number }; timeout: number }
  /** When every vessel carrying this tag is destroyed or safe. */
  | { whenResolved: { tag: string }; timeout: number };

export interface SpawnBeat {
  kind: 'spawn';
  trigger: Trigger;
  /** Identifies this group for later triggers and for shift bookkeeping. */
  tag: string;
  vessel: VesselKind;
  count: number;
  /** Range from the rig, kilometres. */
  rangeKm: number;
  /** Compass bearing in degrees; 0 = +Z. Each extra unit is fanned around this. */
  bearingDeg: number;
  /** Degrees of fan between members of a multi-vessel spawn. */
  spreadDeg?: number;
  /** Friendly only: what it carries. */
  cargo?: 'oil' | 'ammo' | 'repair' | 'crew' | 'torpedo' | 'mixed' | 'hydrophone';
  /**
   * Hostile only: this group attacks the platform itself instead of hunting the convoy.
   * The AI honours it for skiffs, gunboats and FACs; `validateShift` rejects it on any
   * other kind, so a script cannot promise an attack the behaviours would ignore.
   */
  mission?: 'rig';
}

export interface ObjectiveBeat {
  kind: 'objective';
  trigger: Trigger;
  text: string;
}

export interface RadioBeat {
  kind: 'radio';
  trigger: Trigger;
  speaker: string;
  /** What the HUD prints. Clean prose — no direction, no tags. */
  text: string;
  /**
   * The performance script the voice is baked from, with ElevenLabs V3 audio tags.
   *
   * Separate from `text` because the two are read by different things: the player *reads*
   * one and *hears* the other, and `[urgent]` on screen would be a stage direction that
   * escaped onto the stage. Omit it and the line is spoken exactly as written, which is
   * fine for anything that needs no direction.
   */
  read?: string;
}

export interface WeatherBeat {
  kind: 'weather';
  trigger: Trigger;
  preset: WeatherPresetName;
  /** Seconds to blend across. */
  overSeconds: number;
}

export type ShiftBeat = SpawnBeat | ObjectiveBeat | RadioBeat | WeatherBeat;

/**
 * Bearings the rig's own structure masks, measured by sweeping rays from the eye point
 * across the free-look arc at the shallow depression a distant vessel sits at
 * (`window.__dw.maskedBearings()`). Degrees, 0 = dead ahead, positive to starboard.
 *
 * A vessel approaching inside one of these is behind the derrick or the process modules:
 * the player cannot see it resolve and cannot shoot along that line. Friendly arrivals in
 * particular must never be scheduled there — losing a tanker you were never able to
 * defend is not difficulty, it is a broken level.
 */
export const MASKED_BEARINGS: ReadonlyArray<{ from: number; to: number }> = [
  { from: -110, to: -80 },
  { from: 6, to: 63 },
  { from: 82, to: 110 },
];

/** The open corridor between the masked sectors, with a margin. Degrees. */
export const APPROACH_ARC = { min: -76, max: 2 } as const;

export function isBearingMasked(deg: number): boolean {
  return MASKED_BEARINGS.some((s) => deg >= s.from && deg <= s.to);
}

export interface ShiftScript {
  index: number;
  name: string;
  /** Weather at the moment the shift opens. */
  weather: WeatherPresetName;
  /** Deterministic seed for this shift's randomness. */
  seed: string;
  beats: readonly ShiftBeat[];
  /** Hard watchdog: the shift resolves no matter what after this many seconds. */
  maxDuration: number;

  /**
   * `authored` opts this shift out of lull compression entirely: its beats fire at the
   * times they are written at, however fast the player clears the water.
   *
   * The compression exists because an empty sea is usually a defect, and it is right
   * about seven shifts out of eight. It is wrong about the finale, where the empty sea
   * *is* the content. The finale spends 230 seconds turning a storm into a flat calm before
   * anything surfaces, and a player who cleared the opening skiffs in fifteen seconds ran
   * that whole build in sixty-two — four story beats inside twenty-two seconds, with the
   * creature arriving before the sea had finished going quiet. The tell landed after the
   * thing it was telling.
   *
   * It has to be a flag on the script rather than a check on the boss's state: the boss
   * does not exist until it spawns, which is after the damage is done. Endless never sets
   * it — a generated watch has no authored silence to protect.
   */
  pacing?: 'authored';
}

/**
 * Validation run by a unit test over every shipped script. These are the invariants that
 * make a shift finishable; a script that violates one is a bug that would otherwise only
 * surface as a player stuck on a silent ocean.
 */
export function validateShift(script: ShiftScript): string[] {
  const errors: string[] = [];
  const tags = new Set<string>();

  for (const beat of script.beats) {
    if (beat.kind === 'spawn') {
      if (tags.has(beat.tag)) errors.push(`duplicate spawn tag "${beat.tag}"`);
      tags.add(beat.tag);
      if (beat.count < 1) errors.push(`spawn "${beat.tag}" has count ${beat.count}`);
      if (beat.rangeKm <= 0) errors.push(`spawn "${beat.tag}" has non-positive range`);
      if (beat.mission === 'rig'
        && beat.vessel !== 'skiff' && beat.vessel !== 'gunboat' && beat.vessel !== 'fac') {
        errors.push(
          `spawn "${beat.tag}" gives a ${beat.vessel} a rig mission its behaviour ignores`,
        );
      }

      // Every member of a fanned spawn has to clear the rig, not just the centre.
      const spread = beat.spreadDeg ?? 0;
      for (let n = 0; n < beat.count; n++) {
        const offset = beat.count > 1 ? (n - (beat.count - 1) / 2) * spread : 0;
        const bearing = beat.bearingDeg + offset;
        if (isBearingMasked(bearing)) {
          errors.push(
            `spawn "${beat.tag}" member ${n} approaches on ${bearing.toFixed(1)}°, `
            + 'which the rig masks — the player cannot engage along that line',
          );
        }
      }
    }
  }

  for (const beat of script.beats) {
    const t = beat.trigger;
    if ('whenWithin' in t) {
      if (!tags.has(t.whenWithin.tag)) {
        errors.push(`trigger references unknown tag "${t.whenWithin.tag}"`);
      }
      if (!(t.timeout > 0)) errors.push('whenWithin trigger needs a positive timeout');
    } else if ('whenResolved' in t) {
      if (!tags.has(t.whenResolved.tag)) {
        errors.push(`trigger references unknown tag "${t.whenResolved.tag}"`);
      }
      if (!(t.timeout > 0)) errors.push('whenResolved trigger needs a positive timeout');
    }
  }

  if (script.maxDuration <= 0) errors.push('maxDuration must be positive');
  if (!script.beats.some((b) => b.kind === 'spawn')) errors.push('shift has no spawns');

  return errors;
}
